import * as d3Shape from 'd3-shape';
import { CurveString } from '../Types';

type CurveFactory = d3Shape.CurveFactory;

interface Opts {
  closed?: boolean;
  open?: boolean;
}

export const defaultCurve = d3Shape.curveLinear;
export const defaultCurveClosed = d3Shape.curveLinearClosed;

const curveMap = {
  curveBasis: d3Shape.curveBasis,
  curveBasisClosed: d3Shape.curveBasisClosed,
  curveBasisOpen: d3Shape.curveBasisOpen,
  curveBundle: d3Shape.curveBundle,
  curveCardinal: d3Shape.curveCardinal,
  curveCardinalClosed: d3Shape.curveCardinalClosed,
  curveCardinalOpen: d3Shape.curveCardinalOpen,
  curveCatmullRom: d3Shape.curveCatmullRom,
  curveCatmullRomClosed: d3Shape.curveCatmullRomClosed,
  curveCatmullRomOpen: d3Shape.curveCatmullRomOpen,
  curveLinear: d3Shape.curveLinear,
  curveLinearClosed: d3Shape.curveLinearClosed,
  curveMonotoneX: d3Shape.curveMonotoneX,
  curveMonotoneY: d3Shape.curveMonotoneY,
  curveNatural: d3Shape.curveNatural,
  curveStep: d3Shape.curveStep,
  curveStepAfter: d3Shape.curveStepAfter,
  curveStepBefore: d3Shape.curveStepBefore,
};

const curveClosedMap = {
  curveBasis: d3Shape.curveBasisClosed,
  curveBasisClosed: d3Shape.curveBasisClosed,
  curveBasisOpen: d3Shape.curveBasisClosed,
  curveBundle: null,
  curveCardinal: d3Shape.curveCardinalClosed,
  curveCardinalClosed: d3Shape.curveCardinalClosed,
  curveCardinalOpen: d3Shape.curveCardinalClosed,
  curveCatmullRom: d3Shape.curveCatmullRomClosed,
  curveCatmullRomClosed: d3Shape.curveCatmullRomClosed,
  curveCatmullRomOpen: d3Shape.curveCatmullRomClosed,
  curveLinear: d3Shape.curveLinearClosed,
  curveLinearClosed: d3Shape.curveLinearClosed,
  curveMonotoneX: null,
  curveMonotoneY: null,
  curveNatural: null,
  curveStep: null,
  curveStepAfter: null,
  curveStepBefore: null,
};

const curveOpenMap = {
  curveBasis: d3Shape.curveBasis,
  curveBasisClosed: d3Shape.curveBasisOpen,
  curveBasisOpen: d3Shape.curveBasisOpen,
  curveBundle: d3Shape.curveBundle,
  curveCardinal: d3Shape.curveCardinal,
  curveCardinalClosed: d3Shape.curveCardinalOpen,
  curveCardinalOpen: d3Shape.curveCardinalOpen,
  curveCatmullRom: d3Shape.curveCatmullRom,
  curveCatmullRomClosed: d3Shape.curveCatmullRomOpen,
  curveCatmullRomOpen: d3Shape.curveCatmullRomOpen,
  curveLinear: d3Shape.curveLinear,
  curveLinearClosed: d3Shape.curveLinear,
  curveMonotoneX: d3Shape.curveMonotoneX,
  curveMonotoneY: d3Shape.curveMonotoneY,
  curveNatural: d3Shape.curveNatural,
  curveStep: d3Shape.curveStep,
  curveStepAfter: d3Shape.curveStepAfter,
  curveStepBefore: d3Shape.curveStepBefore,
};

function getCurve(): CurveFactory;
function getCurve(curve?: CurveString, opts?: Opts): CurveFactory;
function getCurve<K extends CurveFactory>(curve?: K, opts?: Opts): K;
function getCurve<K extends CurveFactory>(curve?: K | CurveString, opts?: Opts): K | CurveFactory;
function getCurve<K extends CurveFactory>(curve?: K | CurveString, opts: Opts = {}): K | CurveFactory {
  if (typeof curve === 'string') {
    if (opts.closed) {
      return curveClosedMap[curve] || defaultCurveClosed;
    } else if (opts.open && curveOpenMap[curve]) {
      return curveOpenMap[curve] as CurveFactory;
    } else if (curveMap[curve]) {
      return d3Shape[curve] as CurveFactory;
    }

    return defaultCurve;
  }

  return curve || defaultCurve;
}

export default getCurve;
